import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation } from '@tanstack/react-query';
import { History, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { API, errorText } from '../governance_v2/constants';
import { AssigneeRow, KpiRow, ModuleRow, OutcomeRow, Section, Table } from './parts';
import { BudgetVsActual, PeriodComparisons } from './Comparisons';

const OUTCOME_HEAD = ['', 'Tasa de acierto', 'Detalle', 'Logro promedio', 'Desviación promedio'];

const AiNote = ({ companyId }) => {
  const note = useMutation({
    mutationFn: async () => (await axios.post(`${API}/companies/${companyId}/historical/note`)).data,
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-500/[0.05] p-5" data-testid="hist-ai-note">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-5 w-5 text-amber-500" /><p className="mr-auto font-semibold">Nota ejecutiva</p>
        <Button size="sm" variant="outline" onClick={() => note.mutate()} disabled={note.isPending} data-testid="hist-ai-note-btn">{note.isPending ? 'Redactando…' : note.data ? 'Regenerar' : 'Generar nota con IA'}</Button>
      </div>
      {note.data && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700 dark:text-slate-200" data-testid="hist-ai-note-text">{note.data.note}</p>}
      {!note.data && <p className="mt-2 text-xs text-slate-500">La IA solo redacta a partir de las cifras exactas de abajo; no calcula nada.</p>}
    </div>
  );
};

const KpiTrends = ({ kpis }) => {
  const [showAll, setShowAll] = useState(false);
  const reliable = kpis.filter((k) => k.reliable);
  const rest = kpis.filter((k) => !k.reliable);
  return (
    <Section title="Tendencias históricas de KPIs" subtitle="Cambio total, volatilidad (coef. de variación), mejor y peor medición y pendiente mensual (tendencia lineal)" testid="hist-kpis">
      <Table head={['KPI', 'Serie', 'Cambio', 'Volatilidad', 'Mejor', 'Peor', 'Pendiente/mes']} empty="Aún no hay KPIs con 3 o más mediciones."
        rows={[...reliable, ...(showAll ? rest : [])].map((k) => <KpiRow key={`${k.kpi_name}-${k.module_type}`} k={k} />)} />
      {rest.length > 0 && <button type="button" onClick={() => setShowAll((v) => !v)} className="text-xs text-sky-600 hover:underline dark:text-sky-400" data-testid="hist-kpis-toggle">
        {showAll ? 'Ocultar' : 'Mostrar'} {rest.length} KPIs con muestra insuficiente</button>}
    </Section>
  );
};

export const HistoricalPanel = ({ companyId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['historical', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/historical`)).data,
    enabled: !!companyId,
  });
  return (
    <div className="space-y-6" data-testid="historical-panel">
      <div className="flex items-center gap-3">
        <History className="h-7 w-7 text-violet-500" />
        <div><h2 className="text-2xl font-bold text-slate-900 dark:text-white">Inteligencia histórica</h2>
          <p className="text-sm text-slate-500">Estadística exacta sobre lo ejecutado. Con menos de {data?.min_cases ?? 3} casos medidos se marca "muestra insuficiente". SUPER-100 usa estas cifras para ajustar su confianza.</p></div>
      </div>
      {isLoading || !data ? <p className="text-sm text-slate-400">Calculando historial…</p> : (
        <>
          <AiNote companyId={companyId} />
          <Section title="Precisión de agentes" subtitle="Predicciones de proyectos nacidos de propuestas aprobadas vs. el valor real del KPI" testid="hist-agents">
            <Table head={['Agente', ...OUTCOME_HEAD.slice(1)]} empty="Aún no hay predicciones de agentes medidas."
              rows={data.agents.map((a) => <OutcomeRow key={a.agent_name} name={a.agent_name} x={a} testid={`hist-agent-${a.agent_name}`} />)} />
          </Section>
          <Section title="Efectividad de metodologías IAD" subtitle="Decisiones sustentadas por cada IAD que cumplieron su predicción" testid="hist-iads">
            <Table head={['IAD', ...OUTCOME_HEAD.slice(1), 'Usos en análisis']} empty="Aún no hay metodologías usadas."
              rows={data.iads.map((i) => <OutcomeRow key={i.iad_code} name={`${i.iad_code} · ${i.iad_name}`} x={i} testid={`hist-iad-${i.iad_code}`}
                extra={<td className="p-2">{i.uses_in_analyses}</td>} />)} />
          </Section>
          <div className="grid gap-6 xl:grid-cols-2">
            <Section title="Cumplimiento de plazos por módulo" testid="hist-modules">
              <Table head={['Módulo', 'Proyectos', 'SPI promedio', '% atrasados', 'Completados']} empty="Sin proyectos con actividades."
                rows={data.schedule.modules.map((m) => <ModuleRow key={m.module_type} m={m} />)} />
            </Section>
            <Section title="Cumplimiento por responsable" testid="hist-assignees">
              <Table head={['Responsable', 'Actividades', 'Completadas', 'A tiempo', 'Vencidas abiertas']} empty="Sin actividades asignadas."
                rows={data.schedule.assignees.map((a) => <AssigneeRow key={a.user_id} a={a} />)} />
            </Section>
          </div>
          <KpiTrends kpis={data.kpis} />
          <PeriodComparisons kpis={data.kpis} />
          <BudgetVsActual companyId={companyId} items={data.budget_vs_actual || []} />
        </>
      )}
    </div>
  );
};
